'use client'

import {
  EntityDetailSkeleton,
  useArtifactCanvas,
  useDeclareUiSurface,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  EntityAboutSection,
  JobOpeningExperiencesReadSection,
  JobOpeningSkillsReadSection,
  API_ORIGIN,
  useRetryFitAnalysis,
  isCandidateJobsListPathname,
  readListReturnPath,
  readListScrollTop,
  candidateOrganisationHrefForJob,
  navigateWithThreadPreservation,
  publicJobDetailUrl,
  canReapplyAfterWithdraw,
  resolveOpportunityOrganisationLogoUrl,
} from '@yeshuman/sdk'
import type { PublicJob } from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom'

import { ApplicationApplyCanvas } from './application-apply-canvas'
import { CandidateJobDetailHeaderActions } from './candidate-job-detail-header-actions'
import { CandidateJobFitSummaryWidget } from './candidate-job-fit-summary-widget'
import {
  JobOpeningIdentityHeader,
  JobOpeningMetaLine,
  JobOpeningSalaryFooter,
  parseJobOpeningMetaFields,
} from '../job-opening-identity'
import { useSavedOpportunities } from '../../hooks/use-saved-opportunities'
import { buildApplicationStatusMap, useMyApplications } from '../../hooks/use-my-applications'
import { useJobsBoardTenantLogoSrc } from '../../hooks/use-jobs-board-tenant-logo-src'
import {
  type CandidateFromJobState,
  type CandidateJobDetailLocationState,
  type CandidateOrganisationLocationState,
  useCandidateJobsBackTarget,
} from '../../hooks/use-navigate-to-candidate-job-detail'
import type { Opportunity } from '../../hooks/use-opportunities'
import type { OpportunityJobSummaryInput } from '../../utils/opportunity-job-meta'
import { FileText } from 'lucide-react'

function publicJobToJobMeta(job: PublicJob): OpportunityJobSummaryInput {
  return {
    employment_type: job.employment_type,
    work_mode: job.work_mode,
    location: job.location,
    salary_visibility: job.salary_visibility,
  }
}

function publicJobToOpportunity(job: PublicJob): Opportunity {
  return {
    id: job.id,
    title: job.title,
    description: job.description,
    location: job.location ?? '',
    organisation_name: job.organisation_name,
    skills: job.skills ?? [],
    experiences: job.experiences ?? [],
    employment_type: job.employment_type,
    work_mode: job.work_mode,
  }
}

export function CandidateJobDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const applyCanvasKeyRef = useRef(0)
  const {
    savedIds,
    savedOpportunities,
    pendingOpportunityIds,
    stuckOpportunityIds,
    toggleSave,
    isSaving,
    savingOpportunityId,
  } = useSavedOpportunities()
  const { retryCandidate, mutatingOpportunityId } = useRetryFitAnalysis()
  const tenantLogoFallbackSrc = useJobsBoardTenantLogoSrc()
  const jobsBack = useCandidateJobsBackTarget()

  const [job, setJob] = useState<PublicJob | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  useDeclareUiSurface(
    job?.id
      ? {
          intent: 'view',
          entity: 'opportunity',
          id: job.id,
          mode: 'page',
        }
      : null,
  )

  useEffect(() => {
    let cancelled = false
    async function load() {
      const trimmed = slug.trim()
      if (!trimmed) {
        setLoadError('Missing job identifier.')
        setLoading(false)
        return
      }
      setLoading(true)
      setLoadError(null)
      try {
        const response = await fetch(
          `${API_ORIGIN}/api/public/opportunities/${encodeURIComponent(trimmed)}`,
        )
        const data = await response.json()
        if (!response.ok) throw new Error(data.error || 'Job not found')
        if (!cancelled) setJob(data as PublicJob)
      } catch (err) {
        if (!cancelled) {
          setJob(null)
          setLoadError(err instanceof Error ? err.message : 'Job not found')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [slug])

  const { applications, refetch: refetchApplications } = useMyApplications()

  const applicationByOpportunityId = useMemo(
    () => buildApplicationStatusMap(applications),
    [applications],
  )

  const application = job ? applicationByOpportunityId[job.id] ?? null : null
  const opportunityForApply = job ? publicJobToOpportunity(job) : null
  const saved = job ? savedIds.has(job.id) : false

  const backToJobs = () => {
    navigateWithThreadPreservation(
      navigate,
      jobsBack.to,
      searchParams,
      jobsBack.state !== undefined ? { state: jobsBack.state } : undefined,
    )
  }

  const savedRow = useMemo(
    () => (job ? savedOpportunities.find((row) => row.id === job.id) ?? null : null),
    [job, savedOpportunities],
  )
  const showFitWidget = Boolean(job && (savedRow || savedIds.has(job.id)))
  const fitStuck = Boolean(job && stuckOpportunityIds.has(job.id))
  const fitPending = Boolean(
    job &&
      (pendingOpportunityIds.has(job.id) ||
        (!fitStuck && savedIds.has(job.id) && !savedRow)),
  )

  const handleApplyClick = useCallback(() => {
    if (!opportunityForApply) return
    if (application && !canReapplyAfterWithdraw(application.status)) return

    applyCanvasKeyRef.current += 1
    openCanvas({
      title: 'Apply to job opportunity',
      preventAutoFocus: true,
      content: (
        <ApplicationApplyCanvas
          key={`job-detail-apply-${opportunityForApply.id}-${applyCanvasKeyRef.current}`}
          opportunity={opportunityForApply}
          onClose={closeCanvas}
          onApplied={() => void refetchApplications()}
        />
      ),
    })
  }, [application, closeCanvas, openCanvas, opportunityForApply, refetchApplications])

  if (loading) {
    return <EntityDetailSkeleton variant="job" ariaLabel="Loading job" />
  }

  if (loadError || !job) {
    return (
      <PageContainer>
        <PageHeaderLeading
          back={{
            label: jobsBack.label,
            onClick: backToJobs,
          }}
        />
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Job not found</CardTitle>
            <CardDescription alwaysShow>
              {loadError || 'This posting may have been removed or is not published.'}
            </CardDescription>
          </CardHeader>
        </Card>
      </PageContainer>
    )
  }

  const jobMeta = publicJobToJobMeta(job)
  const { salaryText: salaryFromMeta } = parseJobOpeningMetaFields(jobMeta)
  const salaryLabel = salaryFromMeta || job.salary_display?.trim() || ''
  const shareUrl = publicJobDetailUrl(job.slug)
  const organisationLogoUrl =
    resolveOpportunityOrganisationLogoUrl(job, tenantLogoFallbackSrc) ?? null
  const listingProvider = null
  const organisationHref = candidateOrganisationHrefForJob(job.organisation_slug)
  const organisationLinkState: CandidateOrganisationLocationState | undefined = organisationHref
    ? { fromJob: fromJobStateForOrganisation(job, location.state) }
    : undefined

  return (
    <PageContainer>
      <PageHeader
        leading={
          <PageHeaderLeading
            back={{
              label: jobsBack.label,
              onClick: backToJobs,
            }}
          />
        }
        actions={
          <CandidateJobDetailHeaderActions
            shareUrl={shareUrl}
            application={application}
            saved={saved}
            isSaving={isSaving && savingOpportunityId === job.id}
            onSaveToggle={() => toggleSave(job.id, saved)}
            onApply={handleApplyClick}
            onWithdrawn={() => void refetchApplications()}
          />
        }
      />

      <div className="space-y-6">
        <Card>
          <CardContent className="space-y-3">
            <JobOpeningIdentityHeader
              presentation="detail"
              title={job.title}
              showPublicationBadge={false}
              organisationLabel={job.organisation_name}
              organisationLogoUrl={organisationLogoUrl}
              organisationHref={organisationHref}
              organisationLinkState={organisationLinkState}
              listingProvider={listingProvider}
            />
            <JobOpeningMetaLine
              meta={jobMeta}
              publishedAt={job.published_at}
              updatedAt={job.updated_at}
              timestampVariant="interactive"
            />
          </CardContent>
          {salaryLabel ? (
            <CardFooter className="border-t border-border/60">
              <JobOpeningSalaryFooter meta={jobMeta} salaryLabel={job.salary_display} />
            </CardFooter>
          ) : null}
        </Card>

        {showFitWidget ? (
          <CandidateJobFitSummaryWidget
            insight={savedRow?.insight ?? null}
            pending={fitPending}
            stuck={fitStuck}
            onRetry={job ? () => retryCandidate(job.id) : undefined}
            retryPending={job ? mutatingOpportunityId === job.id : false}
          />
        ) : null}

        <EntityAboutSection
          title="Job description"
          leadingIcon={FileText}
          subtitle="Full text for this role"
          body={job.description}
          stripLeadingTitles={[job.title]}
          emptyVariant="text"
          emptyMessage="No description provided."
        />

        <JobOpeningSkillsReadSection
          skills={job.skills ?? []}
          emptyMessage="No skills listed."
        />
        <JobOpeningExperiencesReadSection experiences={job.experiences ?? []} />
      </div>
    </PageContainer>
  )
}

function fromJobStateForOrganisation(
  job: PublicJob,
  locationState: unknown,
): CandidateFromJobState {
  const state = (locationState as CandidateJobDetailLocationState | null) ?? {}
  return {
    slug: job.slug,
    title: job.title,
    ...(state.fromShortlist ? { fromShortlist: true } : {}),
    backPath: readListReturnPath(state, isCandidateJobsListPathname),
    listScrollTop: readListScrollTop(state),
  }
}
